import { ActionContext, ActionTree } from 'vuex';
import { Actions as CommonActions, ActionTypes as CommonActionTypes } from '../Common/actions';

import ApiService from '@/services/ApiService';

import { RootState } from '@/store';
import { State } from './state';
import router from '@/router';

import { Mutations, MutationTypes } from './mutations';
import {
  AuthUser,
  LoginRequest,
  LoginAzureRequest,
  AzureConfigEinstellung,
  RefreshLoginRequest,
  LogoutRequest,
  ArchivUser,
  Benutzer,
} from '@/models/AuthModels';
import { ErrorResponseData } from '@/models/CommonModels';

export enum ActionTypes {
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  LoginAzure = 'LOGINAZURE',
  ValidateOTP = 'VALIDATEOTP',
  RefreshLogin = 'REFRESHLOGIN',
  GetAzureLoginConfig = 'GETAZURELOGINCONFIG',
  SetAzureLoginAvailable = 'SETAZURELOGINAVAILABLE',

  GetArchivUsers = 'GETARCHIVUSERS',
  GetBenutzer = 'GETBENUTZER',
  CreateBenutzer = 'CREATEBENUTZER',
  UpdateBenutzer = 'UPDATEBENUTZER',
  RemoveBenutzer = 'REMOVEBENUTZER',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.Login](context: ActionArguments, data: LoginRequest): Promise<void>;
  [ActionTypes.ValidateOTP](context: ActionArguments, data: LoginRequest): Promise<void>;
  [ActionTypes.RefreshLogin](context: ActionArguments): Promise<void>;
  [ActionTypes.Logout](context: ActionArguments): Promise<void>;

  [ActionTypes.LoginAzure](context: ActionArguments, azureLogin: LoginAzureRequest): Promise<void>;
  [ActionTypes.GetAzureLoginConfig](context: ActionArguments): void;
  [ActionTypes.SetAzureLoginAvailable](context: ActionArguments): void;

  [ActionTypes.GetArchivUsers](context: ActionArguments): Promise<void>;

  [ActionTypes.GetBenutzer](context: ActionArguments): Promise<void>;
  [ActionTypes.CreateBenutzer](context: ActionArguments, data: Benutzer): Promise<void>;
  [ActionTypes.UpdateBenutzer](context: ActionArguments, data: Benutzer): Promise<void>;
  [ActionTypes.RemoveBenutzer](context: ActionArguments, benutzerId: number): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [ActionTypes.Login]({ commit, dispatch, rootGetters }: ActionArguments, data: LoginRequest) {
    const status = rootGetters.status;
    status.authLoading = true;
    status.authErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.login(data)
      .then((res) => {
        const user: AuthUser = res.data;
        commit(MutationTypes.LoginSuccess, user);
        router.push({ name: 'Dashboard' });
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        console.log(errorresponse);
        status.authErrorMsg = errorresponse != null ? errorresponse.title : error.response;
      })
      .finally(() => {
        status.authLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.ValidateOTP]({ commit, dispatch, rootGetters }: ActionArguments, data: LoginRequest) {
    const status = rootGetters.status;
    status.authLoading = true;
    status.authErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.validateOTPLogin(data)
      .then((res) => {
        const user: AuthUser = res.data;
        commit(MutationTypes.LoginSuccess, user);
        router.push({ name: 'Dashboard' });
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        status.authErrorMsg = errorresponse.title;
      })
      .finally(() => {
        status.authLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.RefreshLogin]({ commit, rootGetters }: ActionArguments) {
    const request = new RefreshLoginRequest(rootGetters.authUser.refreshToken);
    return ApiService.refreshLogin(request)
      .then((res) => {
        const result: AuthUser = res.data;
        commit(MutationTypes.RefreshLoginSuccess, result);
      })
      .catch((error) => {
        console.log('REFRESH LOGIN CATCH');
        console.log(error);
      });
  },

  async [ActionTypes.Logout]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.authLoading = true;
    status.authErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    var data = new LogoutRequest(rootGetters.authUser?.refreshToken);
    return ApiService.logout(data)
      .then(() => {
        console.log('LOGOUT');
      })
      .catch((error) => {
        console.log('LOGOUT CATCH');
        console.log(error);
      })
      .finally(async () => {
        commit(MutationTypes.Logout, undefined);

        status.authLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);

        commit(MutationTypes.SetAzureLogin, false);
        sessionStorage.clear();
        const azureconfig = rootGetters.azureconfig;
        const msalConfig = rootGetters.msalConfig;
        const secureBrowserToken = localStorage.getItem('SECBROWSTOKEN');
        const vorlagenViewSection = localStorage.getItem('vorlagenViewSection');
        const textbausteineViewSection = localStorage.getItem('textbausteineViewSection');
        const textvariablenViewSection = localStorage.getItem('textvariablenViewSection');
        const druckvorlagenViewSection = localStorage.getItem('druckvorlagenViewSection');

        localStorage.clear();
        localStorage.setItem('azureconfig', JSON.stringify(azureconfig));
        localStorage.setItem('msalconfig', JSON.stringify(msalConfig));

        if (secureBrowserToken != null) {
          localStorage.setItem('SECBROWSTOKEN', secureBrowserToken);
        }

        if (vorlagenViewSection != undefined) {
          localStorage.setItem('vorlagenViewSection', vorlagenViewSection);
        }

        if (textbausteineViewSection != undefined) {
          localStorage.setItem('textbausteineViewSection', textbausteineViewSection);
        }

        if (textvariablenViewSection != undefined) {
          localStorage.setItem('textvariablenViewSection', textvariablenViewSection);
        }

        if (druckvorlagenViewSection != undefined) {
          localStorage.setItem('druckvorlagenViewSection', druckvorlagenViewSection);
        }
      });
  },

  async [ActionTypes.LoginAzure]({ commit, dispatch, rootGetters }: ActionArguments, azureLogin: LoginAzureRequest) {
    console.log('not implemented yet');
    return;
    // const status = rootGetters.status;
    // status.authLoading = true;
    // status.authErrorMsg = "";
    // dispatch(CommonActionTypes.SetStatus, status);

    // commit(MutationTypes.SetAzureUsername, azureLogin.username)
    // localStorage.setItem('azureusername', JSON.stringify(azureLogin.username));

    // return ArchivApiService.loginAzure(azureLogin.idToken)
    //   .then(async (res) => {
    //     commit(MutationTypes.SetAzureLogin, true)

    //     const user: AuthUser = res.data;
    //     commit(MutationTypes.LoginSuccess, user);
    //     localStorage.setItem("authUser", JSON.stringify(user));

    //     await dispatch(ActionTypes.WMSLogin, undefined)

    //     status.authLoading = false;
    //     status.archivLoggedIn = true;
    //     dispatch(CommonActionTypes.SetStatus, status);

    //     router.push({name: "Dashboard"});
    //   })
    //   .catch(error => {
    //     status.authLoading = false;
    //     status.authErrorMsg = "Anmeldung fehlgeschlagen"
    //     dispatch(CommonActionTypes.SetStatus, status);

    //     localStorage.removeItem('authUser');
    //     localStorage.removeItem('wmsUser');
    //   })
  },

  async [ActionTypes.GetAzureLoginConfig]({ commit }: ActionArguments) {
    console.log('not implemented yet');

    // ApiService.getAzureLoginConfig()
    // .then(res => {
    //   const azureconfig: AzureConfigEinstellung = res.data
    //   commit(MutationTypes.GetAzureLoginConfigSuccess, azureconfig)
    //   localStorage.setItem('azureconfig', JSON.stringify(azureconfig));
    // })
    // .catch(error => {
    //   localStorage.removeItem('azureconfig');
    // })
  },

  [ActionTypes.SetAzureLoginAvailable]({ commit }: ActionArguments) {
    commit(MutationTypes.SetAzureLoginAvailable, undefined);
  },

  async [ActionTypes.GetArchivUsers]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.getArchivUsersLoading = true;
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.getAllArchivUser()
      .then((res) => {
        const users: Array<ArchivUser> = res.data;
        commit(MutationTypes.SetArchivUsers, users);
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        console.log(errorresponse);
      })
      .finally(() => {
        status.getArchivUsersLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetBenutzer]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.getBenutzerLoading = true;
    status.benutzerErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.getAllBenutzer()
      .then((res) => {
        const users: Array<Benutzer> = res.data;
        commit(MutationTypes.SetBenutzer, users);
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        console.log(errorresponse);
        status.benutzerErrorMsg = errorresponse != null ? errorresponse.title : error.response;
      })
      .finally(() => {
        status.getBenutzerLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.CreateBenutzer]({ commit, dispatch, rootGetters }: ActionArguments, data: Benutzer) {
    const status = rootGetters.status;
    status.createBenutzerLoading = true;
    status.benutzerErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.createBenutzer(data)
      .then((res) => {
        const user: Benutzer = res.data;
        commit(MutationTypes.AddOrUpdateBenutzer, user);
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        console.log(errorresponse);
        status.benutzerErrorMsg = errorresponse != null ? errorresponse.title : error.response;
      })
      .finally(() => {
        status.createBenutzerLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateBenutzer]({ commit, dispatch, rootGetters }: ActionArguments, data: Benutzer) {
    const status = rootGetters.status;
    status.updateBenutzerLoading = true;
    status.benutzerErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.updateBenutzer(data)
      .then((res) => {
        const user: Benutzer = res.data;
        commit(MutationTypes.AddOrUpdateBenutzer, user);
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        console.log(errorresponse);
        status.benutzerErrorMsg = errorresponse != null ? errorresponse.title : error.response;
      })
      .finally(() => {
        status.updateBenutzerLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.RemoveBenutzer]({ commit, dispatch, rootGetters }: ActionArguments, id: number) {
    const status = rootGetters.status;
    status.deleteBenutzerLoading = true;
    status.benutzerErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.deleteBenutzer(id)
      .then(() => {
        commit(MutationTypes.RemoveBenutzer, id);
      })
      .catch((error) => {
        var errorresponse = (error.response?.data as ErrorResponseData) || null;
        console.log(errorresponse);
        status.benutzerErrorMsg = errorresponse != null ? errorresponse.title : error.response;
      })
      .finally(() => {
        status.deleteBenutzerLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },
};
